import React from 'react';
import {useTranslation, Trans} from 'react-i18next';
import styled from 'styled-components';
import {ControlButton, TscButtonType, Glyphs, TscThemeNames, TscStyles} from '@techsmith/tsc-cloud-style-guide';
import cssConstants from '../../constants/cssConstants';
import logging from '../../service/logging';
import {StyledWarning} from '../util/StyledElements';
import '../../static/css/modal.less';
import conversationApi from '../../service/conversationApi';
import {IWorkerState} from '../../model/workerState';
import withMemoizedContexts from '../../context/contextContainerHoC';
import {conversationsStore, updateConversationsState} from '../../context/conversationsProvider';
import {IConversationData} from '../../model/conversationModel';
import {updateCommentsState} from '../../context/commentsProvider';
import {updateReactionsState} from '../../context/reactionsProvider';
import {selectedConversationStore, updateSelectedConversationState} from '../../context/selectedConversationProvider';
import {useQueryParam, StringParam} from 'use-query-params';
import Constants from '../../constants/Constants';
import {themeStore} from '../../context/themeProvider';
import {mediaStore} from '../../context/mediaProvider';
import {BasicModalPortal} from '../util/ModalPortalHelper';
import config from '../../service/config';

const BoldText = styled.span`
   font-weight: ${TscStyles.font.weight.semibold};
   padding: 0 0.25rem;
   display: inline-flex;
`;

const FooterButtonsWrapper = styled.div`
   display: flex;
   margin-top: 1.5rem;

   .button-group {
      margin-left: auto;
   }
`;

const DownloadButtonWrapper = styled.div`
   @media screen and (max-width: ${cssConstants.contentBreakpoint}px) {
      > button > span:last-of-type {
         display: none;
      }
   }
`;

export const ClearConversationModalBase: React.FC<IClearConversationModalProps & IStateMappedProps> = ({conversationHash, isDeleting, conversations, VideoReviewId, onClose, theme, Hash}) => {
   const {t} = useTranslation();
   const [conversationParam] = useQueryParam(Constants.queryParams.conversation, StringParam);
   const [state, setState] = React.useState({resetConfirmed: false, isWorking: false, hasError: false} as IClearConversationModalState);

   const onDownloadDataClick = async () => {
      setState({...state, isWorking: true, hasError: false});

      try {
         await conversationApi.downloadSingleConversationData(Hash, conversationHash);
         setState({...state, isWorking: false, hasError: false});
      } catch (e) {
         logging.error(e);
         setState({...state, isWorking: false, hasError: true});
      }
   };

   const clearComments = async () => {
      const newConversation = await conversationApi.deleteConversationComments(Hash, conversationHash);
      const conversationsCopy = Object.assign([], conversations);
      conversationsCopy.splice(conversationsCopy.findIndex(oldConversation => oldConversation.ConversationHash === conversationHash), 1, newConversation);
      updateConversationsState({conversations: conversationsCopy});
      VideoReviewId === newConversation.VideoReviewId && updateCommentsState({comments: []});
   };

   const deleteConversation = async () => {
      await conversationApi.deleteConversation(Hash, conversationHash);
      const conversationsCopy = Object.assign([], conversations);
      const deletedConversation: IConversationData[] = conversationsCopy.splice(conversationsCopy.findIndex(oldConversation => oldConversation.ConversationHash === conversationHash), 1);
      updateConversationsState({conversations: conversationsCopy});
      conversationParam === deletedConversation[0].VideoReviewId && updateSelectedConversationState({VideoReviewId: '', Title: null, SelectedComment: null, ConversationHash: null});

      if (config.featureSwitches.EnableReactions) {
         updateReactionsState({reactions: []});
      }
   };

   const onPrimaryButtonClick = async () => {
      setState(Object.assign({}, state, {isWorking: true, hasError: false}));

      try {
         if (isDeleting) {
            await deleteConversation();
         } else {
            await clearComments();
         }
         onClose();
      } catch (e) {
         logging.error(e);
         setState(Object.assign({}, state, {isWorking: false, hasError: true}));
      }
   };

   const downloadDataButton: React.JSX.Element = (
      <DownloadButtonWrapper>
         <ControlButton
            label={t('resetData.downloadData')}
            buttonType={TscButtonType.tertiary}
            glyph={<Glyphs.Download16x16 />}
            disabled={false}
            themeName={theme}
            onClick={onDownloadDataClick}
            testId={'clear-conversation-modal-download-button'}
         />
      </DownloadButtonWrapper>
   );

   const buttonGroup: React.JSX.Element = (
      <div className="button-group">
         <ControlButton
            label={t('general.cancel')}
            buttonType={TscButtonType.secondary}
            disabled={false}
            themeName={theme}
            width="5.5rem"
            onClick={onClose}
            testId={'clear-conversation-modal-cancel-button'}
         />
         <ControlButton
            label={isDeleting ? t('conversations.modal.delete') : t('conversations.modal.clear')}
            buttonType={TscButtonType.destructive}
            disabled={state.isWorking}
            themeName={theme}
            width="5.5rem"
            onClick={onPrimaryButtonClick}
            testId={'clear-conversation-modal-primary-button'}
         />
      </div>
   );

   return (
      <BasicModalPortal visible={true} title={t(isDeleting ? 'conversations.modal.deleteConversationTitle' : 'conversations.modal.clearCommentsTitle')} width={cssConstants.defaultModalWidth} onClose={onClose} themeName={theme} isLoading={state.isWorking}>
         <div className="modal-body">
            <div className="form-body themeable-section">
               {state.hasError && <StyledWarning>{t('general.errorText')}</StyledWarning>}
               <Trans i18nKey={isDeleting ? config.featureSwitches.EnableReactions ? 'conversations.modal.deleteConversationWithReactionsWarning' : 'conversations.modal.deleteConversationWarning' : 'conversations.modal.clearConversationWarning'}>
                  All <BoldText>comments</BoldText> will be removed. If you want a local backup, download the media data before resetting.
               </Trans>
               <FooterButtonsWrapper>
                  {downloadDataButton}
                  {buttonGroup}
               </FooterButtonsWrapper>
            </div>
         </div>
      </BasicModalPortal>
   );
};

export interface IClearConversationModalProps {
   conversationHash: string;
   isDeleting: boolean;
   onClose(): void;
}

export interface IStateMappedProps {
   conversations: IConversationData[];
   VideoReviewId: string;
   theme: TscThemeNames;
   Hash: string;
}

export interface IClearConversationModalState extends IWorkerState {
   resetConfirmed: boolean;
}

const mapStatesToProps = (
   {conversationHash, isDeleting, onClose}: IClearConversationModalProps,
   {conversations}: Partial<IStateMappedProps>,
   {VideoReviewId}: Partial<IStateMappedProps>,
   {theme}: Partial<IStateMappedProps>,
   {Hash}: Partial<IStateMappedProps>
): IClearConversationModalProps & IStateMappedProps => ({conversationHash, isDeleting, onClose, conversations, VideoReviewId, theme, Hash});

export default withMemoizedContexts(mapStatesToProps, conversationsStore, selectedConversationStore, themeStore, mediaStore)(ClearConversationModalBase);
